import { type Size } from "@/assets/icons/utils";
import { SidebarLogoToggle } from "@/components/SidebarLogoToggle";
import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import { ArchivedSidebarContent } from "@/components/archive/ArchivedSidebar";
import { DesktopTitlebar } from "@/components/desktop/DesktopTitlebar";
import { Button } from "@/components/flexkit/Button";
import { Resizable, ResizableHandle } from "@/components/flexkit/Resizable";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { KeyboardShortcutsModal } from "@/components/keyboard/KeyboardShortcutsModal";
import { GlobalOverloadBanner } from "@/components/nudges/GlobalOverloadBanner";
import { Nudges } from "@/components/nudges/Nudges";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { OrgSwitcherNew } from "@/components/OrgSwitcherNew";
import { cn } from "@/components/podkit/lib/cn";
import { DesktopTrafficLights } from "@/components/sidebar/DesktopTrafficLights";
import { SidebarButton } from "@/components/SidebarButton";
import { Tooltip } from "@/components/Tooltip";
import { SidebarControllerContext } from "@/contexts/SidebarControllerContext";
import { Flags } from "@/feature-flags";
import { useDesktop } from "@/hooks/use-desktop";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useFlag, useFlagOnaAIEnabled, useFlagWorkflowsEnabled, WorkflowAccessLevel } from "@/hooks/use-flag";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useKeyboardHelpShortcut } from "@/hooks/use-keyboard-help-shortcut";
import { useMembership } from "@/hooks/use-membership";
import { useNewEnvironmentShortcut } from "@/hooks/use-open-env-modal-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import { useSidebarToggleShortcut } from "@/hooks/use-sidebar-toggle-shortcut";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { useHasArchivedEnvironments } from "@/queries/environment-queries";
import { CreateEnvironmentModal } from "@/routes/environments/create/CreateEnvironmentModal";
import { SidebarEnvironmentList } from "@/routes/environments/SidebarEnvironmentList";
import { ToggleSidebarKeyBinding } from "@/utils/platform";
import { IconArchive } from "central-icons/IconArchive";
import { IconHomeOpen } from "central-icons/IconHomeOpen";
import { IconLayoutGrid2 } from "central-icons/IconLayoutGrid2";
import { IconPieChart2 } from "central-icons/IconPieChart2";
import { IconSettingsGear2 } from "central-icons/IconSettingsGear2";

import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { CirclePlus, MenuIcon, PanelLeftClose, PanelLeftOpen, XIcon } from "lucide-react";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
    type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { Outlet, useLocation, useMatches } from "react-router-dom";
import type { RouteHandle } from "@/route-handle";
import { NewEnvironmentButton } from "@/components/NewEnvironmentButton";
import { LIST_TRANSITION_CLASSES, TRANSITION_CLASSES, TRANSITION_DURATION } from "@/components/SidebarButton.variants";
import { IconWorkflows } from "@/assets/icons/central/IconAutomations";
import { useAtom } from "jotai";
import { keyboardHelpModalOpenAtom } from "@/atoms/ui-state";

const SIDEBAR_STATE_KEY = "sidebar-state";

type Props = {
    CustomSidebar?: React.ComponentType<{ className?: string; maxWidth?: number; minWidth?: number }>;
    /**
     * Determines whether the sidebar should be collapsible.
     */
    disableCollapse?: boolean;
    disableMobileMenu?: boolean;
    CustomContent?: FC;
};

export const SidebarLayout: FC<Props> = ({
    CustomSidebar,
    disableCollapse = false,
    disableMobileMenu = false,
    CustomContent,
}) => {
    const { isDesktop } = useDesktop();

    // Child routes can specify noPadding via their handle property.
    const matches = useMatches();
    const noPadding = matches.some((match) => (match.handle as RouteHandle | undefined)?.layout?.noPadding === true);

    // Set up dynamic title handling - this will be the fallback for pages that don't set their own title
    useDynamicTitle("Ona");

    const { isMobileViewport } = useIsMobileViewport();
    const location = useLocation();

    const [isDesktopLocked, setIsDesktopLocked] = useState(() => {
        if (disableCollapse) {
            return true; // if we prevent the sidebar from collapsing, we always show it
        }

        const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
        if (savedState === "unlocked") {
            return false;
        }

        return true;
    });

    const isNoSidebar = CustomSidebar === NoSidebar;
    const showGlobalHeader = CustomSidebar !== NoSidebar && isMobileViewport;

    const [showNewEnvModal, setShowNewEnvModal] = useState(false);
    useNewEnvironmentShortcut(() => {
        setShowNewEnvModal(true);
    });
    const [showKeyboardHelp, setShowKeyboardHelp] = useAtom(keyboardHelpModalOpenAtom);
    useKeyboardHelpShortcut(() => {
        setShowKeyboardHelp(true);
    });

    const [isArchiveView, setIsArchiveView] = useState(false);
    const canHoverRef = useRef(true);

    const [playTransition, setPlayTransition] = useState(false);
    const playTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const doPlayTransition = useCallback(() => {
        setPlayTransition(true);
        clearTimeout(playTransitionTimeoutRef.current);
        playTransitionTimeoutRef.current = setTimeout(() => {
            setPlayTransition(false);
        }, TRANSITION_DURATION); // reset the playTransition after transition duration
    }, []);

    const toggleDesktopLock = useCallback(() => {
        const newState = !isDesktopLocked;
        localStorage.setItem(SIDEBAR_STATE_KEY, newState ? "locked" : "unlocked");

        // Use flushSync to ensure state updates happen synchronously
        flushSync(() => {
            setIsDesktopLocked(newState);
            setPlayTransition(true);
        });

        if (!newState) {
            canHoverRef.current = false;
        }

        // Reset transition state after animation completes
        const timeout = setTimeout(() => {
            setPlayTransition(false);
            if (!newState) {
                canHoverRef.current = true;
            }
        }, TRANSITION_DURATION);

        return () => clearTimeout(timeout);
    }, [isDesktopLocked]);

    const lockSidebar = useCallback(() => {
        if (isDesktopLocked) {
            return;
        }

        flushSync(() => {
            setIsDesktopLocked(true);
            setPlayTransition(true);
        });

        clearTimeout(playTransitionTimeoutRef.current);
        playTransitionTimeoutRef.current = setTimeout(() => {
            setPlayTransition(false);
        }, TRANSITION_DURATION);
    }, [isDesktopLocked]);

    const lockSidebarPersistent = useCallback(() => {
        if (!isDesktopLocked) {
            localStorage.setItem(SIDEBAR_STATE_KEY, "locked");

            lockSidebar();
        }
    }, [isDesktopLocked, lockSidebar]);

    const unlockSidebar = useCallback(() => {
        if (!isDesktopLocked) {
            return;
        }

        flushSync(() => {
            setIsDesktopLocked(false);
            setPlayTransition(true);
        });

        canHoverRef.current = false;
        clearTimeout(playTransitionTimeoutRef.current);
        playTransitionTimeoutRef.current = setTimeout(() => {
            setPlayTransition(false);
            canHoverRef.current = true;
        }, TRANSITION_DURATION);
    }, [isDesktopLocked]);

    const unlockSidebarPersistent = useCallback(() => {
        if (isDesktopLocked) {
            localStorage.setItem(SIDEBAR_STATE_KEY, "unlocked");
            unlockSidebar();
        }
    }, [isDesktopLocked, unlockSidebar]);

    const resetSidebar = useCallback(() => {
        const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
        if (savedState === "locked") {
            lockSidebar();
        } else {
            unlockSidebar();
        }
    }, [lockSidebar, unlockSidebar]);

    const sidebarController = useMemo(
        () => ({
            lockSidebar,
            unlockSidebar,
            resetSidebar,
            lockSidebarPersistent,
            unlockSidebarPersistent,
        }),
        [lockSidebar, resetSidebar, unlockSidebar, lockSidebarPersistent, unlockSidebarPersistent],
    );

    // This ensures that we trigger a re-render when the principal changes - this is needed to
    // ensure we re-render when the user is updated.
    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal]);

    // when app.tsx gives us showCreateModal, we know that the URL hash has a relevant context URL for us
    useEffect(() => {
        if (location.state?.showCreateModal) {
            setShowNewEnvModal(true);
        }
    }, [location]);

    useSidebarToggleShortcut(() => {
        if (disableCollapse) {
            return;
        }

        // Exit archive view if active to prevent broken UI state
        if (isArchiveView) {
            setIsArchiveView(false);
        }

        if (isMobileViewport) {
            setMobileMenuOpen(!mobileMenuOpen);
        } else {
            toggleDesktopLock();
        }
    });

    // Cleanup timeouts when component unmounts
    useEffect(() => {
        return () => {
            if (playTransitionTimeoutRef.current != null) {
                clearTimeout(playTransitionTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        doPlayTransition();

        // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when the onboardingForceHideSidebar changes
    }, []);

    // pages like settings disable collapsing, so we run this effect to reset the sidebar state when we are no longer forced to have a locked sidebar
    useEffect(() => {
        if (!disableCollapse) {
            sidebarController.resetSidebar();
        }
    }, [disableCollapse, sidebarController]);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const showSidebar = isMobileViewport ? mobileMenuOpen : true;
    const isLocked = isMobileViewport ? mobileMenuOpen : isDesktopLocked;
    const sidebarClassName = cn(
        playTransition ? TRANSITION_CLASSES : "",
        { "opacity-0": isMobileViewport ? !showSidebar : false },
        { "duration-0": isMobileViewport },
    );
    // When sidebar is not locked, we show only icons (48px width)
    const maxWidth = isMobileViewport ? (showSidebar ? undefined : 0) : !isDesktopLocked ? 48 : undefined;
    const minWidth = isMobileViewport ? (showSidebar ? undefined : 0) : !isDesktopLocked ? 48 : undefined;

    const showHoverHandle = !isDesktopLocked && !isNoSidebar && !isMobileViewport;

    useEffect(() => {
        if (mobileMenuOpen && isMobileViewport) {
            setMobileMenuOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on path change. This effect makes sure that when the user navigates to a new page, the page shows up instead of just the sidebar.
    }, [location.pathname]);

    return (
        <div className="app-background-resetting h-full">
            {/* Skip to main content link for screen readers */}
            <a
                href="#main-content"
                className={cn(
                    "sr-only left-[50%] top-1 z-[9999] rounded bg-surface-01 text-base text-content-primary shadow-lg outline-none ring-1",
                    "focus:not-sr-only focus:!absolute focus:px-3 focus:py-1", // without the `!` focus:not-sr-only and focus:absolute have the same specificity, which would cause our link to be positioned statically
                )}
            >
                Skip to main content
            </a>
            <div className="app-background flex size-full flex-col overflow-hidden">
                <>
                    {/* we only show the titlebar if we're not also showing a header, because in that case, the <Header> renders its own webkit-app-region */}
                    {isDesktop && !showGlobalHeader && <DesktopTitlebar />}
                    {/* Mobile header for new design */}
                    {isMobileViewport && showGlobalHeader && (
                        <header className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center gap-3">
                                {!disableMobileMenu && (
                                    <Button
                                        variant="link"
                                        size="md"
                                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                        className="text-content-primary"
                                        LeadingIcon={mobileMenuOpen ? IconX : IconMenu}
                                        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                                        data-tracking-id="toggle-mobile-menu"
                                    />
                                )}
                                <OnaWordmark className="h-4 w-14" />
                            </div>
                            <Button
                                variant="link"
                                size="md"
                                className="text-content-primary"
                                onClick={() => setShowNewEnvModal(true)}
                                aria-label="New environment"
                                data-tracking-id="new-environment"
                            >
                                <CirclePlus size={20} />
                            </Button>
                        </header>
                    )}
                    {/* persistent global header */}
                    {!isMobileViewport && showGlobalHeader && (
                        <Header
                            toggleLock={toggleDesktopLock}
                            isLocked={isLocked}
                            showSidebarToggle={!disableCollapse}
                            disableSidebarToggle={disableMobileMenu}
                        />
                    )}
                </>
                <div className="flex w-full grow flex-row overflow-hidden">
                    {/* Mobile backdrop overlay */}
                    {isMobileViewport && showSidebar && (
                        <div
                            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                            onClick={() => setMobileMenuOpen(false)}
                            aria-hidden="true"
                            data-tracking-id="close-mobile-menu"
                        />
                    )}
                    {/* Only render sidebar container if sidebar should be shown or we're not on mobile */}
                    {(showSidebar || !isMobileViewport) && (
                        <div
                            className="relative flex-shrink-0"
                            onMouseEnter={() => {
                                if (!isDesktopLocked && canHoverRef.current && !isMobileViewport) {
                                    doPlayTransition();
                                }
                            }}
                            onMouseLeave={() => {
                                setPlayTransition(false);
                            }}
                        >
                            <div
                                className={cn(
                                    "h-full overflow-hidden pt-2",
                                    {
                                        "app-background fixed inset-y-0 left-0 z-50": isMobileViewport && showSidebar,
                                    }, // mobile overlay for new design
                                    { invisible: !showSidebar && !playTransition }, // remove from AT when not shown
                                )}
                                data-track-location={TrackLocations.Sidebar}
                            >
                                {CustomSidebar ? (
                                    <CustomSidebar
                                        className={sidebarClassName}
                                        maxWidth={maxWidth}
                                        minWidth={minWidth}
                                    />
                                ) : (
                                    <Sidebar
                                        isArchiveView={isArchiveView}
                                        onArchiveViewChange={setIsArchiveView}
                                        toggleLock={toggleDesktopLock}
                                        isLocked={isLocked}
                                        handleOpenNewEnvModal={() => setShowNewEnvModal(true)}
                                        className={sidebarClassName}
                                        maxWidth={maxWidth}
                                        minWidth={minWidth}
                                        mobileMenuOpen={mobileMenuOpen}
                                        setMobileMenuOpen={setMobileMenuOpen}
                                    />
                                )}
                            </div>
                            <div
                                className={cn("h-full w-2 cursor-pointer", {
                                    hidden: !showHoverHandle,
                                })} // we only help hovering on desktop and when there is content to reveal
                            />
                        </div>
                    )}
                    <div
                        className={cn(
                            "size-full flex-grow overflow-hidden p-2",
                            { "pl-0": (showHoverHandle || isDesktopLocked) && !isNoSidebar && !isMobileViewport }, // on desktop, this space is taken up by the "hover handle" above
                            { "pt-0": isMobileViewport && !disableMobileMenu },
                        )}
                    >
                        <SidebarControllerContext.Provider value={sidebarController}>
                            {CustomContent ? (
                                <CustomContent />
                            ) : (
                                <>
                                    <GlobalOverloadBanner />
                                    <MainContent noPadding={noPadding}>
                                        <Outlet />
                                    </MainContent>
                                </>
                            )}
                        </SidebarControllerContext.Provider>
                    </div>
                    <div
                        className={cn("fixed bottom-14 left-10 z-20", {
                            invisible: !showSidebar && !playTransition,
                        })}
                    >
                        <Nudges setIsArchiveView={setIsArchiveView} />
                    </div>
                </div>
            </div>

            <CreateEnvironmentModal
                open={showNewEnvModal}
                onClose={() => setShowNewEnvModal(false)}
                data-track-location={TrackLocations.SidebarCreateEnvironmentModal}
                initialAutoStart={!!location.state?.autoStart}
            />
            <KeyboardShortcutsModal open={showKeyboardHelp} onOpenChange={setShowKeyboardHelp} />
        </div>
    );
};

export const NoSidebar: FC = () => {
    return <div />;
};

export const ResizableSidebar: FC<{
    children: ReactNode;
    className?: string;
    maxWidth?: number;
    minWidth?: number;
}> = ({ children, className, maxWidth, minWidth }) => {
    const { isMobileViewport } = useIsMobileViewport();
    // On mobile, don't use Resizable component when sidebar should be hidden
    if (isMobileViewport && maxWidth === 0) {
        return null;
    }

    return (
        <Resizable
            initialWidth={300}
            maxWidth={maxWidth ?? 500}
            minWidth={minWidth ?? 296}
            autoSaveId="sidebar-width"
            className={cn("h-full", className)}
            fullWidthThreshold={768}
        >
            {children}
        </Resizable>
    );
};

export const Sidebar: FC<{
    isArchiveView: boolean;
    onArchiveViewChange: (isArchive: boolean) => void;
    toggleLock: () => void;
    isLocked: boolean;
    handleOpenNewEnvModal: () => void;
    className?: string;
    maxWidth?: number;
    minWidth?: number;
    mobileMenuOpen: boolean;
    setMobileMenuOpen: (open: boolean) => void;
}> = ({
    isArchiveView,
    onArchiveViewChange,
    toggleLock,
    isLocked,
    handleOpenNewEnvModal,
    className,
    maxWidth,
    minWidth,
    mobileMenuOpen,
    setMobileMenuOpen,
}) => {
    const { isMobileViewport } = useIsMobileViewport();

    return (
        <ResizableSidebar maxWidth={maxWidth} minWidth={minWidth} className={className}>
            <div data-testid="sidebar" className="flex size-full flex-col pb-[6px] pt-0.5">
                {isArchiveView ? (
                    <ArchivedSidebarContent onArchiveViewChange={onArchiveViewChange} />
                ) : (
                    <>
                        {/* ONA Logo Section */}
                        {!isMobileViewport && <SidebarLogoToggle isLocked={isLocked} onToggle={toggleLock} />}
                        {isMobileViewport && (
                            <div className="flex items-center justify-between px-2 pb-0 pt-2">
                                <div className="h-8 w-8" />
                                <Tooltip
                                    content={
                                        <div className="flex flex-row items-center gap-2">
                                            <span>{isLocked ? "Close menu" : "Open menu"}</span>
                                            <KeyboardCombo keys={ToggleSidebarKeyBinding} />
                                        </div>
                                    }
                                    usePortal
                                >
                                    <Button
                                        variant="ghost"
                                        className="h-8 w-8 rounded-[4px] border-0 p-0"
                                        size="sm"
                                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                        data-tracking-id="toggle-sidebar-menu"
                                    >
                                        {mobileMenuOpen ? <IconX size="base" /> : <IconMenu size="base" />}
                                    </Button>
                                </Tooltip>
                            </div>
                        )}
                        <ScrollArea
                            orientation="vertical"
                            className={cn("mr-0.5 flex-grow pr-0 pt-2", {
                                "mr-1.5 pr-0.5 ![scrollbar-gutter:auto]": !isLocked,
                                "pr-[1px]": isLocked, // add enough space for focus rings
                            })}
                        >
                            <SidebarContent handleOpenNewEnvModal={handleOpenNewEnvModal} isLocked={isLocked} />
                        </ScrollArea>
                        <div className={cn("p-2 pb-2 pt-4")}>
                            <SidebarFooter
                                onArchiveViewChange={onArchiveViewChange}
                                isLocked={isLocked}
                                toggleLock={toggleLock}
                            />
                        </div>
                    </>
                )}
            </div>
            <ResizableHandle
                className={cn("top-4 mr-[1px] h-[calc(100%-32px)]", {
                    "!cursor-e-resize": !isLocked,
                })}
                onClick={() => {
                    if (!isLocked) {
                        toggleLock();
                    }
                }}
                data-tracking-id-none
            />
        </ResizableSidebar>
    );
};

const IconPanelLeftOpen: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <PanelLeftOpen size={sizeMap[size]} />;
};

const IconPanelLeftClose: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <PanelLeftClose size={sizeMap[size]} />;
};

const IconX: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <XIcon size={sizeMap[size]} />;
};

const IconMenu: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <MenuIcon size={sizeMap[size]} />;
};

type HeaderProps = {
    toggleLock: () => void;
    isLocked: boolean;
    showSidebarToggle: boolean;
    disableSidebarToggle?: boolean;
};
const Header: FC<HeaderProps> = ({ toggleLock, isLocked, showSidebarToggle, disableSidebarToggle = false }) => {
    return (
        <div
            className={cn(
                "flex h-9 flex-shrink-0 flex-row items-center justify-between gap-2 border-transparent bg-transparent px-5",
            )}
        >
            <div className="flex gap-2">
                <DesktopTrafficLights />
                {showSidebarToggle && (
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                <span>{isLocked ? "Close sidebar" : "Lock sidebar open"}</span>
                                <KeyboardCombo keys={ToggleSidebarKeyBinding} className="bg-surface-invert-tertiary" />
                            </div>
                        }
                    >
                        <Button
                            variant="link"
                            size="sm"
                            onClick={disableSidebarToggle ? undefined : toggleLock}
                            LeadingIcon={isLocked ? IconPanelLeftClose : IconPanelLeftOpen}
                            className="text-content-primary focus-visible:outline-content-primary"
                            aria-label={isLocked ? "Close sidebar" : "Lock sidebar open"}
                            disabled={disableSidebarToggle}
                            data-tracking-id="toggle-sidebar-lock"
                        />
                    </Tooltip>
                )}
                <div className="flex min-w-16">
                    <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} />
                </div>
            </div>
            <div className="h-full flex-grow [-webkit-app-region:drag]" />
        </div>
    );
};

export const SidebarFooter: FC<{
    onArchiveViewChange?: (isArchive: boolean) => void;
    isLocked: boolean;
    toggleLock: () => void;
}> = ({ onArchiveViewChange, isLocked, toggleLock }) => {
    const { hasArchivedEnvironments } = useHasArchivedEnvironments();
    const location = useLocation();
    const isSettingsPage = location.pathname.startsWith("/settings");

    // Helper to render Settings button
    const settingsContent = useMemo(() => {
        if (isSettingsPage) {
            return null;
        }

        return (
            <SidebarButton
                icon={<IconSettingsGear2 size={20} />}
                label="Settings"
                to="/settings"
                hideLabel={!isLocked}
            />
        );
    }, [isSettingsPage, isLocked]);

    const footerBaseClasses = cn("flex flex-col gap-1 w-full");

    if (onArchiveViewChange) {
        return (
            <div className={cn(footerBaseClasses, "gap-2")}>
                <div className="flex flex-col gap-1">
                    <SidebarButton
                        icon={<IconArchive size={20} />}
                        label="Archive"
                        onClick={() => {
                            if (!isLocked) {
                                toggleLock();
                            }
                            onArchiveViewChange(true);
                        }}
                        showBadge={hasArchivedEnvironments}
                        hideLabel={!isLocked}
                        data-tracking-id="show-archive-view"
                    />
                    {settingsContent}
                </div>
                {!isSettingsPage && (
                    <OrgSwitcherNew data-track-location={TrackLocations.SidebarOrgSwitcher} isLocked={isLocked} />
                )}
            </div>
        );
    }

    // Default state: Show settings and org switcher
    return (
        <div className={cn(footerBaseClasses, "gap-2")}>
            {settingsContent}
            {!isSettingsPage && (
                <OrgSwitcherNew data-track-location={TrackLocations.SidebarOrgSwitcher} isLocked={isLocked} />
            )}
        </div>
    );
};

const SidebarContent: FC<{ handleOpenNewEnvModal: () => void; isLocked: boolean }> = ({
    handleOpenNewEnvModal,
    isLocked,
}) => {
    const location = useLocation();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const { value: isInsightsDisabled } = useFlag(Flags.InsightsDisabled, false);
    const { accessLevel: workflowAccessLevel } = useFlagWorkflowsEnabled();
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    return (
        <>
            <div className="flex flex-col gap-1 pl-2">
                <NewEnvironmentButton
                    isLocked={isLocked}
                    onClick={handleOpenNewEnvModal}
                    data-tracking-id="open-new-env-modal"
                />
                {isOnaEnabled && (
                    <SidebarButton
                        icon={<IconHomeOpen size={20} />}
                        label={"Home"}
                        active={location.pathname === "/ai"}
                        to="/ai"
                        hideLabel={!isLocked}
                    />
                )}
                <SidebarButton
                    icon={<IconLayoutGrid2 size={20} />}
                    label="Projects"
                    active={location.pathname === "/projects"}
                    to="/projects"
                    hideLabel={!isLocked}
                />
                {workflowAccessLevel !== WorkflowAccessLevel.None && (
                    <SidebarButton
                        icon={<IconWorkflows />}
                        label="Automations"
                        active={location.pathname.startsWith("/automations")}
                        to="/automations"
                        hideLabel={!isLocked}
                    />
                )}
                {!isInsightsDisabled && isOrgAdmin && (
                    <SidebarButton
                        icon={<IconPieChart2 size={20} />}
                        label="Insights"
                        active={location.pathname === "/insights"}
                        to="/insights"
                        hideLabel={!isLocked}
                    />
                )}
                <div className="h-2" />
                <Lists isLocked={isLocked} />
            </div>
        </>
    );
};

const Lists = ({ isLocked }: { isLocked: boolean }) => {
    return (
        <div className={cn(LIST_TRANSITION_CLASSES, { "hidden h-0 overflow-hidden": !isLocked })}>
            <SidebarEnvironmentList />
        </div>
    );
};

type MainContentProps = PropsWithChildren<{ noPadding?: boolean }>;

export const MainContent: FC<MainContentProps> = ({ children, noPadding }) => {
    const Comp = noPadding ? "div" : ScrollArea;

    return (
        <main
            id="main-content"
            className={cn("size-full overflow-hidden rounded-lg border border-border-base bg-surface-01 p-0.5", {
                "p-0": noPadding,
            })}
        >
            <Comp
                orientation="both"
                className={cn("size-full max-w-full", {
                    "p-0": noPadding,
                    "p-6": !noPadding,
                })}
            >
                {children}
            </Comp>
        </main>
    );
};
